import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The `useDelayedActiveState` is used to provide a delayed
 * synchronization of a boolean to an input boolean. It's purpose
 * is to facilitate certain behaviors pertaining to areas such as
 * animation.
 *
 * Example scenario - On an `isActive` state I want to render
 * some element, I also want to run CSS animations at the same time,
 * if this occurs all at once, the transitions won't take effect
 * as it was mounted at the same time. By staggering the output
 * of this change, we can apply a second value shortly after render
 * to provide the necessary delay
 *
 * @param {boolean} isActive - whether is active
 * @param {number} delayActive - the delay before activating
 * @param {number} delayInactive - the delay before inactivating
 * @param {boolean} matchInitial - whether to match initial state
 * @returns {boolean} the delayed active state
 */
const useDelayedActiveState = (
  isActive,
  delayActive = 0.01,
  delayInactive = 0,
  matchInitial = false
) => {
  const wasActive = useRef(matchInitial ? isActive : false)
  const timeout = useRef()
  const [isDelayedActive, setIsDelayedActive] = useState(
    matchInitial ? isActive : false
  )
  const isMounted = useRef()

  /**
   * Run the trigger
   */
  const trigger = useCallback((isActive, delay) => {
    if (!delay) {
      // No delay, set immediately
      setIsDelayedActive(isActive)

      return
    }

    timeout.current = setTimeout(() => {
      if (!isMounted.current) {
        // Defensive
        return
      }

      setIsDelayedActive(isActive)
    }, delay * 1000)
  }, [])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
      clearTimeout(timeout.current)
    }
  }, [])

  /**
   * Effect: When the `isActive` state changes
   */
  useEffect(() => {
    if (isActive === wasActive.current) {
      return
    }

    wasActive.current = isActive
    clearTimeout(timeout.current)

    if (isActive) {
      trigger(isActive, delayActive)

      return
    }

    trigger(isActive, delayInactive)
  }, [isActive, delayActive, delayInactive])

  return isDelayedActive
}

// Export
export default useDelayedActiveState
